import React, { useState, useCallback } from 'react';
import { Popover } from '@mantine/core';
import { useEventListener } from 'ahooks';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { useTempState } from '@starflow/app/store';
import { Tooltip } from '@mantine/core';
import { BASETYPE_VALUE_TO_LABEL, BASETYPES_FILTER_OPTIONS } from '@shakkerai/pro-models';

const baseTypeOptions = [
  {
    value: '',
    label: translate('common_sort4'),
  },
].concat(
  BASETYPES_FILTER_OPTIONS.map((item) => {
    return {
      value: `${item.value}`,
      label: item.label,
      items: item.items,
      itemsValuesMap: item.items ? Object.fromEntries(item.items.map((subItem) => [subItem.value, true])) : {},
    };
  }),
);

const BaseTypesPicker = React.memo(({ value, onChange }) => {
  const globaoMode = useTempState('globalMode');
  const [opened, setOpened] = useState(false);
  const selectedValue = value?.length ? value[0] : '';

  const handleChange = (baseTypes) => onChange(baseTypes.filter(Boolean));

  const setPickerOpen = () => {
    setOpened(!opened);
  };

  const handleGlobalClick = useCallback((event) => {
    if (event.target?.closest(`[data-role="model-base-type-picker-entry"]`)) {
      return;
    }

    if (event.target?.closest(`[data-role="model-base-type-picker-wrapper"]`)) {
      return;
    }

    setOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  return (
    <Popover opened={opened} position="bottom-end" shadow="xl">
      <Popover.Target>
        {globaoMode === 'pro' ? (
          <button
            data-role="model-base-type-picker-entry"
            onClick={() => setPickerOpen()}
            className="flex flex-shrink-0 items-center text-[13px] text-[#2d2d2d] font-normal"
          >
            <span>{selectedValue ? BASETYPE_VALUE_TO_LABEL[selectedValue] : translate('common_sort4')}</span>
            <Icon className={`ml-1 shk-kuangjia-xiala ${opened ? 'rotate-180' : ''}`} fontSize={18} color="inherit" />
          </button>
        ) : (
          <Tooltip
            withArrow
            className="translate-y-[5px] scale-90 bg-[#848484]"
            label={translate('preset_basemodle_type')}
          >
            <button
              data-role="model-base-type-picker-entry"
              className="flex items-center text-[13px] text-[#ADADAD] font-normal cursor-not-allowed flex-shrink-0"
            >
              <span>{translate('common_sort4')}</span>
              <Icon className={`ml-1 shk-kuangjia-xiala`} fontSize={18} color="#ADADAD" />
            </button>
          </Tooltip>
        )}
      </Popover.Target>
      <Popover.Dropdown w={132} classNames={{ dropdown: 'p-0 border-none rounded-sm shadow-light' }}>
        <div data-role="model-base-type-picker-wrapper" className="p-1.5">
          {baseTypeOptions.map((item) => {
            let selected = false;

            if (selectedValue) {
              selected = selectedValue === item.value || item.itemsValuesMap?.[selectedValue];
            } else {
              selected = item.value === '';
            }

            const handleClick = () => {
              setOpened(false);

              if (selected) return;
              if (item.items) return;

              handleChange([item.value]);
            };

            return (
              <button
                key={item.value}
                onClick={handleClick}
                className="group relative flex items-center w-[120px] h-[30px] bg-white hover:bg-[#f4f6f8] rounded-sm px-1.5 text-xs"
              >
                <span className={`${selected ? 'text-primary' : ' text-[#2d2d2d2]'} text-xs`}>{item.label}</span>
                {!!item.items && (
                  <Icon
                    className={`icon-border_right ml-1 ${selected ? 'text-primary' : 'text-[#2d2d2d]'}`}
                    fontSize={12}
                  />
                )}
                {!!item.items && (
                  <div className="absolute top-0 left-full hidden group-hover:block">
                    <div className="ml-1.5 p-1.5 bg-white rounded-sm shadow-light">
                      {item.items.map((subItem) => (
                        <button
                          key={subItem.value}
                          disabled={selectedValue === subItem.value}
                          onClick={() => handleChange([subItem.value])}
                          className="flex items-center w-[120px] h-[30px] bg-white hover:bg-[#f4f6f8] rounded-sm px-1.5 text-[#2d2d2d2] disabled:text-primary text-xs"
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});

export default BaseTypesPicker;
